/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import { RaaRoles, sessionUserHasRolePrivileges } from '../../../../../../utility/SecurityRules';
import DeleteConfirmationModal from '../../../../../common/DeleteConfirmationModal';
import { connectModalContainerContextToComponent } from '../../../../../../contexts/ModalContext/ModalContext';
import notificationGroupPropType from '../../../../../../utility/PropTypes/notificationGroupPropType';
import { CrudType } from '../../../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../../../utility/types/CrudStatus';
import {
  deleteNotificationGroup,
  selectNotificationGroupsCrudOperation,
  selectNotificationGroupsCrudStatus,
} from '../../../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';
import NotificationGroupDetailsModal from '../NotificationGroupDetailsModal/NotificationGroupDetailsModal';
import NotificationGroupUtility from '../../../../../../utility/NotificationGroupUtility';
import envConfig from '../../../../../../envConfig';
import CannotDeleteInUseModal from '../../../../../common/CannotDeleteInUseModal';
import { BulkAlertingResourceType } from '../../../../../../utility/types/BulkAlertingResourceType';
import { setAppliedAlertSearch } from '../../../../../../redux/slices/alertListSlice/alertListSlice';
import FacetId from '../../../../../../utility/types/facetType';
import CrudItemContextMenu from '../../../../../common/RaaLists/CrudItemContextMenu/CrudItemContextMenu';
import { selectSessionInformation } from '../../../../../../redux/slices/sessionSlice';
import { selectUserList } from '../../../../../../redux/slices/userListSlice/userListSlice';

function NotificationGroupsTableRow({ notificationGroup, setModalJSX }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const crudOperation = useSelector(selectNotificationGroupsCrudOperation);
  const crudStatus = useSelector(selectNotificationGroupsCrudStatus);
  const users = useSelector(selectUserList);
  const getUsedInCount = () => notificationGroup && notificationGroup.uniqueAlertCount > 0;
  const recipientsString = NotificationGroupUtility
    .getRecipientDetailsStringArray(notificationGroup.recipients, users);
  const deleteInProgress = crudOperation === CrudType.DELETE && crudStatus === CrudStatus.PENDING;
  const { activeSubscription } = useSelector(selectSessionInformation);

  const handleView = () => {
    setModalJSX(
      <NotificationGroupDetailsModal notificationGroup={notificationGroup} />,
    );
  };

  const handleSafeDelete = () => {
    const isInUse = getUsedInCount();
    if (isInUse && isInUse > 0) {
      setModalJSX(
        <CannotDeleteInUseModal
          itemName={notificationGroup.name}
          itemType={BulkAlertingResourceType.NOTIFICATION_GROUP}
        />,
      );
    }
    setModalJSX(
      <DeleteConfirmationModal
        itemName={notificationGroup.name}
        isButtonDisabled={deleteInProgress}
        handleDelete={() => {
          dispatch(deleteNotificationGroup(notificationGroup.id));
          setModalJSX(<></>);
        }}
      />,
    );
  };

  return (
    <tr onClick={handleView} style={{ cursor: 'pointer' }}>
      <td className="ellipsis width-25">
        <span className="ml-1">
          {notificationGroup.name}
        </span>
      </td>
      <td className="ellipsis width-25" title={recipientsString}>
        <span className="ml-1 pr-1">
          {
              notificationGroup.recipients.length
                ? recipientsString
                : <span>No recipients</span>
        }
        </span>
      </td>
      <td className="width-15" onClick={(e) => e.stopPropagation()}>
        <span className="ml-1">
          <button
            type="button"
            className="btn btn-link p-0"
            title="View in alerts list"
            onClick={async () => {
              await dispatch(setAppliedAlertSearch({
                filters: [
                  {
                    id: null,
                    facetId: FacetId.NOTIFICATION_GROUP,
                    operatorId: OperatorTypes.EQUAL_TO_VALUE,
                    value1: notificationGroup.id.toString(),
                  },
                ],
              }));
              history.push('/s/alerts');
            }}
          >
            {notificationGroup.uniqueAlertCount}
            {' '}
            {notificationGroup.uniqueAlertCount === 1 ? 'alert' : 'alerts'}
          </button>
        </span>
      </td>
      <td className="width-10" onClick={(e) => e.stopPropagation()}>
        <CrudItemContextMenu
          rowData={notificationGroup}
          onEditClick={() => {
            window.location.assign(
              `${envConfig.raaUrl}/s/actions/notification-groups/edit/${notificationGroup.id}`,
            );
          }}
          onDeleteClick={(event) => handleSafeDelete(event)}
          onCloneClick={() => {
            window.location.assign(
              `${envConfig.raaUrl}/s/actions/notification-groups/new/${notificationGroup.id}`,
            );
          }}
          onViewClick={handleView}
          hasEditPrivileges={
            sessionUserHasRolePrivileges(activeSubscription,
              [RaaRoles.ADMIN, RaaRoles.MANAGER])
          }
          isNotificationGroup
          onTestActionClick={handleView}
        />
      </td>
    </tr>
  );
}

NotificationGroupsTableRow.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  notificationGroup: PropTypes.shape(notificationGroupPropType).isRequired,
};
export default connectModalContainerContextToComponent(NotificationGroupsTableRow);
